import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { useMemo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { Tile, TileContent, TileStickyHeader } from '~/ui/core/tile/Tile';

import { type JobProfileTileSubject, JobProfileTileTabBar, type JsaProfileTileTab } from '../../../job-architecture-v2';
import { PositionCloseHeader } from '../../../position-management';
import { useUrlTileEscapeClose } from '../../../url-tiles';
import { useStrategyGroupItems } from '../api/use-strategy-group-items';
import {
    byLevel,
    ItemsSkeleton,
    PositionItems,
    type ReadyScope,
    TaskItems,
    useRoleTilePositionScope,
} from './RoleTilePanels';

const Fact: FunctionComponent<{ readonly label: string; readonly value: ReactNode }> = ({ label, value }) => (
    <div className="flex min-w-0 items-baseline justify-between gap-m py-s">
        <dt className="shrink-0 text-body-s text-text-secondary">{label}</dt>
        <dd className="m-0 min-w-0 text-right text-body-s text-text-primary [overflow-wrap:anywhere]">{value}</dd>
    </div>
);

/** The title, where the role or level sits, and its Overview, Tasks and Positions tabs, all from its positions. */
const ScopedContent: FunctionComponent<{ readonly initialTab: JsaProfileTileTab; readonly scope: ReadyScope }> = ({
    initialTab,
    scope,
}) => {
    const translate = useTranslate();
    const positions = useStrategyGroupItems(scope);
    const firstPage = positions.data?.pages[0];
    const first = firstPage?.items[0];
    const isRole = scope.grouping === 'ROLE';
    const title = (isRole ? first?.role : first?.level)?.title;
    const placement = [first?.jobFamily?.title, first?.jobCluster?.title].filter(is.string);
    const levels = useMemo(
        () =>
            isRole
                ? byLevel(positions.data?.pages.flatMap((page) => page.items) ?? []).map(([id, group]) => ({
                      count: group.items.length,
                      id,
                      title: group.title,
                  }))
                : [],
        [isRole, positions.data],
    );

    const overview = (
        <dl className="m-0 flex flex-col divide-y divide-border-soft">
            {first?.jobFamily ? (
                <Fact
                    label={translate('pages.ai-transformation.strategy.filter.job-family')}
                    value={first.jobFamily.title}
                />
            ) : null}
            {first?.jobCluster ? (
                <Fact
                    label={translate('pages.ai-transformation.strategy.filter.job-cluster')}
                    value={first.jobCluster.title}
                />
            ) : null}
            {levels.length === 0 ? null : (
                <Fact
                    label={translate('pages.ai-transformation.strategy.view.levels')}
                    value={levels.map((level) => `${level.title} (${level.count})`).join(', ')}
                />
            )}
            {firstPage === undefined ? null : (
                <Fact label={translate('pages.ai-transformation.strategy.view.positions')} value={firstPage.total} />
            )}
        </dl>
    );

    return (
        <>
            <div className="mt-3xl flex min-w-0 flex-col gap-xxs">
                {title === undefined ? (
                    <SkeletonBase className="h-[1.5rem] w-3/5 rounded-lg" />
                ) : (
                    <h2 className="m-0 text-[1.375rem] font-medium text-text-primary [overflow-wrap:anywhere]">
                        {title}
                    </h2>
                )}
                {placement.length === 0 ? null : (
                    <span className="text-body-s text-text-secondary">{placement.join(' · ')}</span>
                )}
            </div>
            <div className="mt-6xl">
                <JobProfileTileTabBar
                    automations={undefined}
                    initialTab={initialTab}
                    overview={overview}
                    positions={<PositionItems scope={scope} />}
                    tasks={<TaskItems scope={scope} />}
                />
            </div>
        </>
    );
};

/**
 * The role or level tile for a user who may not read the Job Architecture, such as a transformation lead: built from
 * the positions the user may read, so it fires no Job Architecture read.
 */
export const RoleTileWithoutJobArchitecture: FunctionComponent<
    JobProfileTileSubject & { readonly initialTab: JsaProfileTileTab; readonly onClose: () => void }
> = ({ initialTab, onClose, ...subject }) => {
    const translate = useTranslate();
    const scope = useRoleTilePositionScope(subject);

    useUrlTileEscapeClose(onClose);

    return (
        <Tile data-testid="role-tile-without-job-architecture">
            <ScrollArea
                className="min-w-0 flex-1"
                scrollFade="vertical"
                scrollFadeSize="var(--spacing-4xl)"
                viewportClassName="[&>div]:block!"
            >
                <TileContent className="min-h-full w-full">
                    <div className="flex w-full min-w-0 flex-col">
                        <TileStickyHeader compact={true}>
                            <PositionCloseHeader onClose={onClose} />
                        </TileStickyHeader>
                        {scope.status === 'ready' ? (
                            <ScopedContent initialTab={initialTab} scope={scope} />
                        ) : scope.status === 'pending' ? (
                            <ItemsSkeleton label={translate('pages.ai-transformation.strategy.group-tile.loading')} />
                        ) : (
                            <EmptyStateMessage>
                                {translate('pages.company-home.journey.headline.no-positions')}
                            </EmptyStateMessage>
                        )}
                    </div>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};
